import { apiUrl } from "../constants/const";
import ENDPOINTS from "./endpoints";

/**
 * Scheduler API
 *
 * CRUD for scheduled prompt jobs. All endpoints require X-Session-Token.
 * org_id / user_id are resolved server-side from the auth context.
 */

const getHeaders = () => ({
  "Content-Type": "application/json",
  "X-Session-Token": localStorage.getItem("session_token") || "",
});

/**
 * List all scheduled jobs for the authenticated user.
 * @returns {Promise<Array>} Array of job objects
 */
export const listSchedulerJobs = async () => {
  const response = await fetch(`${apiUrl}${ENDPOINTS.SCHEDULER_JOBS}`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to list scheduler jobs: ${response.status}`);
  }

  const data = await response.json();
  return data.jobs || [];
};

/**
 * Create a new scheduled prompt job.
 * @param {Object} params
 * @param {string} params.spark_id
 * @param {string} [params.space_id] Spectra space used for server-side owner resolution
 * @param {string} params.prompt
 * @param {string} params.cron_expression  - Key from SCHEDULE_OPTIONS
 * @param {string} [params.timezone]
 * @param {string} [params.message_id]
 * @param {string} params.source_prompt_id Selected attempt's immutable prompt ID
 * @param {string} params.source_answer_id Selected answer ID
 * @param {string} params.turn_id Stable source turn ID; validated server-side
 * @param {string} [params.label]
 * @returns {Promise<Object>} Created job
 */
export const createSchedulerJob = async (params) => {
  const response = await fetch(`${apiUrl}${ENDPOINTS.SCHEDULER_JOBS}`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `Failed to create scheduler job: ${response.status}`);
  }

  return response.json();
};

/**
 * Update an existing scheduled job.
 * @param {Object} params
 * @param {string} params.job_id
 * @param {string} [params.prompt]
 * @param {string} [params.cron_expression]
 * @param {string} [params.timezone]
 * @param {string} [params.label]
 * @param {boolean} [params.enabled]
 * @returns {Promise<Object>} Updated job
 */
export const updateSchedulerJob = async ({ job_id, ...body }) => {
  const response = await fetch(`${apiUrl}${ENDPOINTS.SCHEDULER_JOB(job_id)}`, {
    method: "PATCH",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to update scheduler job: ${response.status}`);
  }

  return response.json();
};

/**
 * Delete a scheduled job.
 * @param {string} job_id
 */
export const deleteSchedulerJob = async (job_id) => {
  const response = await fetch(`${apiUrl}${ENDPOINTS.SCHEDULER_JOB(job_id)}`, {
    method: "DELETE",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to delete scheduler job: ${response.status}`);
  }

  return response.json();
};

/**
 * List all scheduled jobs across the org (admin only).
 * @returns {Promise<Array>} Array of job objects
 */
export const listAdminSchedulerJobs = async () => {
  const response = await fetch(`${apiUrl}${ENDPOINTS.ADMIN_SCHEDULER_JOBS}`, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to list org scheduler jobs: ${response.status}`);
  }

  const data = await response.json();
  return data.jobs || [];
};
